import React from 'react';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import InlineEdit from '@atlaskit/inline-edit';
import { Inline, Box, xcss, Bleed, Text } from '@atlaskit/primitives';
import Textfield from '@atlaskit/textfield';
import { UNSCHEDULED_COLUMN_ID } from '@atlassian/jira-common-constants/src/column-types';
import { useIntl } from '@atlassian/jira-intl';
import {
	convertCapacity,
	getCapacityUnitMessage,
} from '@atlassian/jira-portfolio-3-plan-increment-common/src/common/utils';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { IP_BOARD_DAYS_PLANNING_UNIT } from '../../common/constants';
import commonMessages from '../../common/messages';
import type { Props } from './types';

const CapacityCount = ({
	columnId,
	totalCapacity,
	usedCapacity,
	defaultCapacity,
	planningConfig: { planningUnit, workingHoursPerDay },
	onChangeCapacity,
	hideTotalCapacity,
	isReadOnly,
}: Props) => {
	const { formatMessage } = useIntl();
	const total = convertCapacity(totalCapacity ?? defaultCapacity, planningUnit, workingHoursPerDay);
	const unitLabel = getCapacityUnitMessage(planningUnit, formatMessage, total);
	const showOverCapacityStyle = usedCapacity > total;
	const { createAnalyticsEvent } = useAnalyticsEvents();

	if (columnId === UNSCHEDULED_COLUMN_ID || hideTotalCapacity) {
		return (
			<Inline alignBlock="center" xcss={capacityContainerStyles}>
				<Text
					color="inherit"
					testId="portfolio-3-plan-increment-common.ui.capacity-count.allocated-capacity"
				>
					{usedCapacity}
				</Text>
				&nbsp;
				<Text
					color="inherit"
					testId="portfolio-3-plan-increment-common.ui.capacity-count.capacity-unit"
				>
					{unitLabel}
				</Text>
			</Inline>
		);
	}

	if (isReadOnly) {
		return (
			<Inline
				alignBlock="center"
				xcss={
					showOverCapacityStyle
						? [overCapacityStyles, capacityContainerStyles]
						: capacityContainerStyles
				}
			>
				<Text
					color="inherit"
					testId="portfolio-3-plan-increment-common.ui.capacity-count.allocated-capacity"
				>
					{usedCapacity}
				</Text>
				&nbsp; / &nbsp;
				<Text
					color="inherit"
					testId="portfolio-3-plan-increment-common.ui.capacity-count.total-capacity-read-only"
				>
					{total}
				</Text>
				&nbsp;
				<Text
					color="inherit"
					testId="portfolio-3-plan-increment-common.ui.capacity-count.capacity-unit"
				>
					{unitLabel}
				</Text>
			</Inline>
		);
	}

	return (
		<Inline
			alignBlock="center"
			xcss={
				showOverCapacityStyle
					? [overCapacityStyles, capacityContainerStyles]
					: capacityContainerStyles
			}
		>
			<Text
				color="inherit"
				testId="portfolio-3-plan-increment-common.ui.capacity-count.allocated-capacity"
			>
				{usedCapacity}
			</Text>
			&nbsp; /&nbsp;
			<Bleed xcss={inlineEditStyles}>
				<InlineEdit
					defaultValue={total}
					keepEditViewOpenOnBlur
					editView={({ errorMessage, ...fieldProps }) => (
						<Textfield
							{...fieldProps}
							testId="portfolio-3-plan-increment-common.ui.capacity-count.total-capacity-edit"
							min={0}
							max={99999}
							width="60"
							aria-label={formatMessage(commonMessages.editCapacityLabel)}
							isCompact
							autoFocus
							type="number"
							{...(planningUnit === IP_BOARD_DAYS_PLANNING_UNIT && { step: 0.1 })}
						/>
					)}
					readView={() => (
						<Box
							testId="portfolio-3-plan-increment-common.ui.capacity-count.total-capacity-read-only"
							xcss={
								showOverCapacityStyle
									? [readViewContainerStyles, overCapacityStyles]
									: readViewContainerStyles
							}
						>
							{total}
						</Box>
					)}
					onConfirm={(value: string) => {
						onChangeCapacity(value);
						fireUIAnalytics(
							createAnalyticsEvent({ action: 'edited', actionSubject: 'iterationCapacity' }),
						);
					}}
					editButtonLabel={formatMessage(commonMessages.editCapacityLabel)}
					confirmButtonLabel={formatMessage(commonMessages.confirmButtonLabel)}
					cancelButtonLabel={formatMessage(commonMessages.cancelButtonLabel)}
				/>
			</Bleed>
			&nbsp;
			<Text
				color="inherit"
				testId="portfolio-3-plan-increment-common.ui.capacity-count.capacity-unit"
			>
				{unitLabel}
			</Text>
		</Inline>
	);
};

export default CapacityCount;

const capacityContainerStyles = xcss({
	whiteSpace: 'nowrap',
	flexShrink: 0,
});

const readViewContainerStyles = xcss({
	paddingBlock: 'space.050',
	paddingInline: 'space.050',
	lineHeight: '20px',
});

const overCapacityStyles = xcss({
	color: 'color.text.warning',
});

const inlineEditStyles = xcss({
	marginBlockStart: 'space.negative.100',
	flexShrink: 0,
});
